/**
 * login-prompt-tooltip
 *
 * CSS handles hover and keyboard focus on pointer devices. This adds the parts
 * CSS cannot: tap-to-toggle where there is no hover, dismissal, and keeping
 * `aria-expanded` truthful.
 *
 * Registered as a custom element so it works for markup that arrives after load
 * — section rendering in the theme editor, and the sticky header, which is
 * re-rendered rather than moved.
 *
 * It also pushes four GTM dataLayer events: `login_prompt_eligible`,
 * `login_prompt_impression`, `login_prompt_open` and `login_prompt_click`. See
 * the tracking notes above `#trackOnce` for why `eligible` and `impression` are
 * separate, and why opens are not simply read off `#setOpen`.
 */

/** Milliseconds the pointer must rest on the trigger before an open counts.
 *
 *  Not arbitrary caution: the first caller puts this tooltip in a sticky header
 *  immediately beside the Add to Cart button, so the pointer crosses the icon
 *  on the way to the button many times a session. Without a dwell, the great
 *  majority of `login_prompt_open` events would be mice in transit rather than
 *  anyone reading the bubble. */
const HOVER_DWELL_MS = 200;

/** Fired-once bookkeeping, keyed by prompt id and deliberately at module scope
 *  rather than per instance.
 *
 *  The sticky header is re-rendered rather than moved, which destroys and
 *  recreates the element. Per-instance state would call each of those a fresh
 *  impression and inflate the count for a tooltip the visitor never scrolled
 *  away from. Keying by prompt id means "once per prompt per page view", which
 *  is what the numbers need to mean for open-rate (opens ÷ impressions) to be
 *  worth reading.
 *
 *  Two tooltips sharing an id therefore also share dedupe — the same collision
 *  the snippet already warns about for `aria-describedby`. */
const fired = {
  eligible: new Set(),
  impression: new Set(),
  open: new Set(),
};

/**
 * @param {string} name  Event name, without the `login_prompt_` prefix.
 * @param {Record<string, unknown>} detail
 */
function pushPromptEvent(name, detail) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: `login_prompt_${name}`,
    ...detail,
  });
}

class LoginPromptTooltip extends HTMLElement {
  #trigger = null;
  #bubble = null;
  #open = false;
  #observer = null;
  #hoverTimer = null;

  connectedCallback() {
    this.#trigger = this.querySelector('[data-login-prompt-trigger]');
    if (!this.#trigger) return;

    this.#bubble = this.querySelector('[data-login-prompt-bubble]');

    this.#trigger.addEventListener('click', this.#onTriggerClick);
    this.addEventListener('keydown', this.#onKeydown);
    // Focus leaving the component entirely closes it. Tabbing from the trigger
    // INTO the link does not, because focusout fires before the new target is
    // focused and `relatedTarget` is what tells the two apart.
    this.addEventListener('focusout', this.#onFocusOut);

    // Tracking. These are separate from the behaviour listeners above because
    // they observe states CSS owns, which the behaviour code never sees.
    this.addEventListener('mouseenter', this.#onPointerEnter);
    this.addEventListener('mouseleave', this.#onPointerLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('click', this.#onLinkClick);

    // Fires for every rendered tooltip, visible or not — see `eligible` in the
    // tracking notes. Deliberately before the impression observer, because it
    // must not depend on anything the stylesheet decides.
    this.#trackOnce('eligible');
    this.#watchForImpression();
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.#onTriggerClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('mouseenter', this.#onPointerEnter);
    this.removeEventListener('mouseleave', this.#onPointerLeave);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('click', this.#onLinkClick);
    document.removeEventListener('click', this.#onDocumentClick);

    this.#observer?.disconnect();
    this.#observer = null;
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = null;
  }

  get #isTouch() {
    return window.matchMedia('(hover: none)').matches;
  }

  #onTriggerClick = (event) => {
    // On a pointer device CSS already shows the bubble on hover, so a click
    // would toggle something that is visible and look like nothing happened.
    if (!this.#isTouch) return;

    event.preventDefault();
    event.stopPropagation();
    this.#setOpen(!this.#open);
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#open && !this.contains(document.activeElement)) return;

    this.#setOpen(false);
    // Focus goes back to the trigger rather than nowhere: dismissing with the
    // keyboard should not drop the caret at the top of the document.
    this.#trigger?.focus();
  };

  #onFocusOut = (event) => {
    if (this.contains(event.relatedTarget)) return;
    this.#setOpen(false);
  };

  #onDocumentClick = (event) => {
    if (this.contains(event.target)) return;
    this.#setOpen(false);
  };

  #setOpen(open) {
    if (open === this.#open) return;

    this.#open = open;
    this.classList.toggle('is-open', open);
    this.#trigger?.setAttribute('aria-expanded', String(open));

    // Only listen while open. A document-level listener per tooltip, always on,
    // is a cost every tap on the page pays for a component nobody opened.
    if (open) {
      // Any other open tooltip closes, so a tap never leaves two on screen.
      for (const other of document.querySelectorAll('login-prompt-tooltip.is-open')) {
        if (other !== this) other.close();
      }
      document.addEventListener('click', this.#onDocumentClick);
      // The tap path. Hover and keyboard opens never reach here — see below.
      this.#trackOnce('open', { open_method: 'tap' });
    } else {
      document.removeEventListener('click', this.#onDocumentClick);
    }
  }

  /** Closes the tooltip. Public so sibling instances can dismiss each other. */
  close() {
    this.#setOpen(false);
  }

  /* ── Tracking ──────────────────────────────────────────────────────────
   *
   * `eligible` and `impression` look redundant and are not. Keep both.
   *
   *   eligible   — the tooltip rendered into the DOM. The Liquid gate passed:
   *                logged out, on a product with an upgrade path. Fires
   *                regardless of whether CSS shows the thing.
   *   impression — it was actually on screen.
   *
   * They diverge because the A/B test hides the control group with
   * `display: none`, so control fires `eligible` and never `impression`. That
   * is the only way to identify "control users who would have seen it", which
   * is the comparison group the test needs. Collapse the two into one event and
   * the experiment loses its denominator — there is then no way to tell a
   * control user who was eligible from one who was never in the running.
   *
   * An "open" cannot be read off `#setOpen`, which is the obvious place to put
   * it and would be wrong. `#setOpen` only ever runs on touch devices:
   * `#onTriggerClick` returns early when the device has hover, because on a
   * pointer device the stylesheet has already shown the bubble via
   * `:hover` / `:focus-within` without telling JavaScript anything. Hooking
   * only `#setOpen` would report zero desktop opens while looking correct.
   *
   * So the three ways the bubble can appear are tracked where they each
   * actually happen: `tap` in `#setOpen`, `hover` on mouseenter (after a
   * dwell), and `focus` on focusin for keyboard users.
   */

  /** Context for the dataLayer, supplied by the snippet as data attributes so
   *  each caller can label its own placement without this file knowing about
   *  loyalty pricing, the sticky header, or whatever calls it next. */
  get #eventDetail() {
    const {
      promptId,
      promptPlacement,
      promptContext,
      promptProductId,
      promptProductHandle,
      promptProductTitle,
    } = this.dataset;

    const detail = {
      prompt_id: promptId || 'login-prompt',
      // Resolved here rather than read straight off the attribute. The caller
      // asks for a placement, but under 750px the stylesheet overrides every
      // bubble to below-centre regardless — so the attribute describes what was
      // requested and this describes what the visitor was actually shown.
      // Reporting the request would put `left` on every mobile event.
      prompt_placement: this.#resolvedPlacement(promptPlacement),
    };

    if (promptContext) detail.prompt_context = promptContext;
    if (promptProductId) detail.product_id = promptProductId;
    // Sent alongside the numeric id because the two join to different things:
    // the id to Shopify's own exports, the handle to GA4, where it is the part
    // of `page_location` that identifies the product. GA4's `item_id` comes
    // from the Google channel app and is not this bare number, so joining these
    // events to item-level reports goes through the handle.
    if (promptProductHandle) detail.product_handle = promptProductHandle;
    if (promptProductTitle) detail.product_title = promptProductTitle;

    return detail;
  }

  #resolvedPlacement(requested) {
    if (window.matchMedia('(max-width: 749px)').matches) return 'below';
    return requested || null;
  }

  get #dedupeKey() {
    return this.dataset.promptId || 'login-prompt';
  }

  /** Pushes `name` at most once per prompt id per page view. */
  #trackOnce(name, detail = {}) {
    const key = this.#dedupeKey;
    if (fired[name].has(key)) return;

    fired[name].add(key);
    pushPromptEvent(name, { ...this.#eventDetail, ...detail });
  }

  #watchForImpression() {
    if (fired.impression.has(this.#dedupeKey)) return;

    // Without IntersectionObserver there is no honest way to say the tooltip
    // was seen, and claiming an impression for every render would overcount
    // against the desktop numbers it would be compared with. Skip instead.
    if (!('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          this.#trackOnce('impression');
          // One impression is all this observer exists for.
          this.#observer?.disconnect();
          this.#observer = null;
        }
      },
      // Half the trigger, which is an 18px icon: enough that it is genuinely on
      // screen, loose enough that a sticky header clipped a pixel at the
      // viewport edge still counts.
      { threshold: 0.5 }
    );

    this.#observer.observe(this);
  }

  #onPointerEnter = () => {
    // Touch devices report a hover on tap, which would double-count against the
    // `tap` event `#setOpen` already fires.
    if (this.#isTouch) return;

    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = setTimeout(() => {
      this.#trackOnce('open', { open_method: 'hover' });
    }, HOVER_DWELL_MS);
  };

  #onPointerLeave = () => {
    // Left before the dwell elapsed: the pointer was passing through.
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = null;
  };

  #onFocusIn = () => {
    // `:focus-within` has shown the bubble to a keyboard user. No dwell here —
    // tabbing to the trigger is deliberate in a way that crossing it is not.
    this.#trackOnce('open', { open_method: 'focus' });
  };

  #onLinkClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !this.#bubble?.contains(link)) return;

    // Not deduped, unlike impression and open. A second click is a second
    // attempt to reach login and worth seeing, and the click navigates away
    // regardless, so there is no runaway to guard against.
    //
    // The push is synchronous, but the GTM tag's own network request can still
    // be cut short by the navigation that follows. If any of these come up
    // short against the login page's own pageviews, set that tag's transport
    // to `beacon` in GTM — it is a tag setting, not something this file can fix
    // without delaying the link and making the prompt feel broken.
    const detail = this.#eventDetail;

    pushPromptEvent('click', {
      ...detail,
      link_url: link.href,
      link_text: link.textContent?.trim() || null,
    });

    // Claim credit for whatever login follows. `login-attribution` holds this
    // across the navigation and the Shopify-hosted auth pages, then attaches it
    // to `login_completed` when the visitor comes back signed in — which is the
    // only point at which this prompt can be said to have worked.
    //
    // Optional on purpose: a caller that renders the tooltip without the
    // attribution snippet still gets `login_prompt_click`, just no credit for
    // the login.
    window.LoginAttribution?.record({
      prompt_id: detail.prompt_id,
      prompt_context: detail.prompt_context || null,
      product_id: detail.product_id || null,
      product_handle: detail.product_handle || null,
    });
  };
}

if (!customElements.get('login-prompt-tooltip')) {
  customElements.define('login-prompt-tooltip', LoginPromptTooltip);
}
